import type { KeyboardEvent as ReactKeyboardEvent } from "react";

import {
  BUCKET_COLOR_VAR,
  type EmailCardData,
  relativeTime,
} from "@/lib/emails";
import { cn } from "@/lib/cn";

interface EmailCardProps {
  email: EmailCardData;
  onSelect?: (id: string) => void;
}

const PRIORITY_CLASSES: Record<string, string> = {
  P0: "border-[color:var(--op-amber)] text-[color:var(--op-amber)]",
  P1: "border-op-border-accent text-op-accent",
  P2: "border-op-border text-op-secondary",
  P3: "border-op-border text-op-muted",
};

export function EmailCard({ email, onSelect }: EmailCardProps) {
  const senderLabel = email.from_name || email.from_email || "Unknown";
  // Chip prefers a curated client tag; falls back to the resolved sender name
  // so we never render "UNKNOWN SENDER" again.
  const chipLabel =
    (email.client_name && email.client_name.trim()) ||
    email.sender_display_name ||
    "Unknown sender";
  const time = relativeTime(email.received_at);
  const stripeColor = BUCKET_COLOR_VAR[email.bucket];
  const interactive = !!onSelect;

  const handleClick = () => onSelect?.(email.id);
  const handleKey = (e: ReactKeyboardEvent<HTMLElement>) => {
    if (!onSelect) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(email.id);
    }
  };

  return (
    <article
      className={cn(
        "relative rounded-lg border border-op-border bg-op-surface pl-3 pr-3 py-3 transition-colors",
        interactive &&
          "cursor-pointer hover:bg-op-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-op-accent",
      )}
      data-testid="email-card"
      data-bucket={email.bucket}
      data-email-id={email.id}
      onClick={interactive ? handleClick : undefined}
      onKeyDown={interactive ? handleKey : undefined}
      tabIndex={interactive ? 0 : undefined}
      role={interactive ? "button" : undefined}
      aria-label={interactive ? `Open ${email.subject || "email"}` : undefined}
    >
      <span
        aria-hidden
        className="absolute left-0 top-0 h-full w-1 rounded-l-lg"
        style={{ backgroundColor: stripeColor }}
      />
      <header className="flex items-center justify-between gap-2 pl-1">
        <span
          className="truncate rounded-full border border-op-border px-2 py-0.5 text-[10px] uppercase tracking-wider text-op-secondary"
          title={chipLabel}
        >
          {chipLabel}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          {email.is_overridden ? (
            <span
              data-testid="email-card-pinned"
              title="Pinned by you"
              aria-label="Pinned by you"
              className="font-mono text-[10px] text-op-accent"
            >
              PIN
            </span>
          ) : null}
          <span
            className={cn(
              "rounded border px-1.5 py-0.5 font-mono text-[10px]",
              PRIORITY_CLASSES[email.priority] ?? PRIORITY_CLASSES.P3,
            )}
          >
            {email.priority}
          </span>
          <span className="text-[11px] text-op-muted">{time}</span>
        </div>
      </header>
      <h3
        className="mt-2 truncate pl-1 text-sm font-medium text-op-primary"
        title={email.subject}
      >
        {email.subject || "(no subject)"}
      </h3>
      <p className="truncate pl-1 text-xs text-op-secondary" title={senderLabel}>
        {senderLabel}
      </p>
      {email.summary_line_1 ? (
        <p className="mt-2 pl-1 text-xs leading-snug text-op-secondary">
          {email.summary_line_1}
        </p>
      ) : null}
      {email.summary_line_2 ? (
        <p className="pl-1 text-xs leading-snug text-op-muted">
          {email.summary_line_2}
        </p>
      ) : null}
    </article>
  );
}
